"use strict";
// packages/shared-schemas/src/websiteRoutingPreview.ts
//
// Which rotation a website form's leads reach, computed from the same pure
// matchers pickAssignee uses, so the connection page can show it before a
// lead is lost. A setup no rotation can take (the Cavara stage connection
// was company-wide while its only rotation was head office) used to save
// silently and leave every enquiry unassigned.
//
// What the preview knows: the form's fixed service category and office, its
// preset answers, the tenant's rotations, cascade order, routing_v2 mode,
// territory switch and fallback owners. What it cannot know: the visitor's
// own answers. So it reports
//   - `rotation`: where a lead with only the form's fixed values lands;
//   - `answerRotations`: rotations that need particular answers (loan
//     amount band, purpose, ...) and can take some of this form's leads
//     instead, under routing_v2 enforce;
//   - `ignoredAnswerRotations`: the same rotations when routing_v2 is off or
//     observe, where they never take effect (v1 hides them).
// Availability, leave, quiet hours and geo rules are runtime state and are
// not modelled; this is about configuration that can never match.
//
// Pure: no I/O, no clock.
Object.defineProperty(exports, "__esModule", { value: true });
exports.previewRoutingV2Mode = previewRoutingV2Mode;
exports.previewWebsiteFormRouting = previewWebsiteFormRouting;
const leadClassification_1 = require("./leadClassification");
const leadRoutingMatch_1 = require("./leadRoutingMatch");
const leadRoutingMatchV2_1 = require("./leadRoutingMatchV2");
const leadTaxonomy_1 = require("./leadTaxonomy");
const leadTerritories_1 = require("./leadTerritories");
/** Missing, malformed and unknown values read as 'off', exactly as the server does. */
function previewRoutingV2Mode(value) {
    return value === 'observe' || value === 'enforce' ? value : 'off';
}
function presetClassification(preset) {
    const c = (0, leadClassification_1.emptyClassification)();
    if (!preset)
        return c;
    c.loanPurpose = preset.loanPurpose || null;
    c.propertyUse = preset.propertyUse || null;
    c.loanStructure = preset.loanStructure || null;
    c.loanAmount = preset.loanAmount || null;
    c.borrowerSituations = [...(preset.borrowerSituations ?? [])].filter(Boolean);
    return c;
}
/** Service and office constraints of `rule` admit the target (answers aside). */
function scopeAdmits(rule, target) {
    const m = rule.match ?? {};
    if (m.serviceCategory && m.serviceCategory !== target.serviceCategory)
        return false;
    if (m.officeId && m.officeId !== target.officeId)
        return false;
    // A classification rule with no service category never matches (no tier for it).
    return !!m.serviceCategory;
}
function previewWebsiteFormRouting(rules, routingV2Mode, target) {
    const rotations = rules?.rotations ?? [];
    const fallbackOwnerCount = new Set(rules?.fallbackOwnerUids ?? []).size;
    if (rules && (0, leadTerritories_1.isTerritoryRoutingEnabled)({
        territoryRoutingEnabled: rules.territoryRoutingEnabled,
        postcodeTerritories: rules.postcodeTerritories,
    })) {
        return { kind: 'territory_triage', fallbackOwnerCount };
    }
    const mode = previewRoutingV2Mode(routingV2Mode);
    const classification = presetClassification(target.preset);
    const dealType = (0, leadClassification_1.legacyDealTypeFromClassification)(classification);
    const ruleSet = { rotations: [...rotations], cascadePriority: [...(rules?.cascadePriority ?? leadTaxonomy_1.DEFAULT_CASCADE_PRIORITY)] };
    const baseline = mode === 'enforce'
        ? (0, leadRoutingMatchV2_1.matchLeadRotationV2)({ serviceCategory: target.serviceCategory, officeId: target.officeId, dealType, classification }, ruleSet)?.rule ?? null
        : (0, leadRoutingMatch_1.matchLeadRotation)((0, leadRoutingMatchV2_1.legacyRotationRulesOnly)(ruleSet), {
            serviceCategory: target.serviceCategory,
            officeId: target.officeId,
            dealType,
        });
    const answerRules = rotations
        .filter((r) => !(0, leadRoutingMatchV2_1.isLegacyRotationRule)(r) && r.id !== baseline?.id && scopeAdmits(r, target))
        .map((r) => ({ id: r.id, name: r.name }));
    const answerRotations = mode === 'enforce' ? answerRules : [];
    const ignoredAnswerRotations = mode === 'enforce' ? [] : answerRules;
    if (!baseline)
        return { kind: 'none', fallbackOwnerCount, answerRotations, ignoredAnswerRotations };
    return { kind: 'rotation', rotation: { id: baseline.id, name: baseline.name }, answerRotations, ignoredAnswerRotations };
}
